import { Box, BoxProps } from '@mui/material';

export interface DevBannerProps {
  /**
   * Custom environment label. If not provided, it will be auto-detected
   * from VITE_APP_ENV or MODE environment variables.
   */
  envLabel?: string;
  /**
   * Position of the banner. Defaults to 'bottom-right'.
   */
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
  /**
   * Background color of the banner. Defaults to '#ff6b35'.
   */
  backgroundColor?: string;
  /**
   * Text color of the banner. Defaults to '#fff'.
   */
  textColor?: string;
  /**
   * Additional sx props for the container.
   */
  sx?: BoxProps['sx'];
}

/**
 * DevBanner - A 45-degree ribbon banner on a corner of the screen
 * indicating this is a development/staging environment.
 * Only renders when not in production mode (unless envLabel is explicitly provided).
 */
export const DevBanner: React.FC<DevBannerProps> = ({
  envLabel: customEnvLabel,
  position = 'bottom-right',
  backgroundColor = '#ff6b35',
  textColor = '#fff',
  sx,
}) => {
  // Detect environment from Vite build-time environment variables.
  // IMPORTANT: We must access import.meta.env directly (not through a variable)
  // so Vite can statically replace these values at build time.
  // Using dynamic access patterns (like `typeof import.meta`) prevents inlining.
  const appEnv = import.meta.env.VITE_APP_ENV || import.meta.env.MODE || 'development';
  const isProduction = appEnv === 'production';

  // Only show in non-production environments unless explicitly overridden
  if (isProduction && !customEnvLabel) {
    return null;
  }

  const envLabel = customEnvLabel || (appEnv === 'development' ? 'DEV' : appEnv.toUpperCase());

  // Calculate position styles based on position prop
  const getPositionStyles = () => {
    switch (position) {
      case 'bottom-left':
        return {
          container: { bottom: 0, left: 0 },
          banner: { bottom: 25, left: -50, transform: 'rotate(45deg)' },
        };
      case 'top-right':
        return {
          container: { top: 0, right: 0 },
          banner: { top: 25, right: -50, transform: 'rotate(45deg)' },
        };
      case 'top-left':
        return {
          container: { top: 0, left: 0 },
          banner: { top: 25, left: -50, transform: 'rotate(-45deg)' },
        };
      case 'bottom-right':
      default:
        return {
          container: { bottom: 0, right: 0 },
          banner: { bottom: 25, right: -50, transform: 'rotate(-45deg)' },
        };
    }
  };

  const posStyles = getPositionStyles();

  return (
    <Box
      sx={{
        position: 'fixed',
        width: 150,
        height: 150,
        overflow: 'hidden',
        pointerEvents: 'none',
        zIndex: 99999,
        ...posStyles.container,
        ...sx,
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          width: 200,
          backgroundColor,
          color: textColor,
          textAlign: 'center',
          fontWeight: 700,
          fontSize: '12px',
          letterSpacing: '1px',
          padding: '6px 0',
          boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
          textTransform: 'uppercase',
          ...posStyles.banner,
        }}
      >
        {envLabel} SITE
      </Box>
    </Box>
  );
};

export default DevBanner;
